import React, { useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Container,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Model, surveyLocalization, SurveyModel } from 'survey-core';
import { Survey as SurveyComponent } from 'survey-react-ui';
import { PlainLightPanelless } from 'survey-core/themes';
import 'survey-core/i18n/french';
import { observer } from 'mobx-react';
import { modals } from '@mantine/modals';
import {
  IconAlertCircle,
  IconCheck,
  IconDownload,
  IconMail,
} from '@tabler/icons-react';
import { useGetPendingPhysicianEICFConsents } from '../../../global/entities/EICFConsents/queries/useGetPendingPhysicianEICFConsents.tsx';
import { useGetSignedPhysicianEICFConsents } from '../../../global/entities/EICFConsents/queries/useGetSignedPhysicianEICFConsents.tsx';
import { useSubmitEICFPhysicianSignature } from '../../../global/entities/EICFConsents/mutations/useSubmitEICFPhysicianSignature.tsx';
import { useDownloadEICFConsentPdf } from '../../../global/entities/EICFConsents/mutations/useDownloadEICFConsentPdf.tsx';
import { useSendEICFConsentToAssistant } from '../../../global/entities/EICFConsents/mutations/useSendEICFConsentToAssistant.tsx';
import {
  EICFConsentStatus,
  EICFConsentTemplateKind,
  IEICFConsent,
} from '../../../global/entities/EICFConsents/EICFConsent.ts';
import { AppMedError } from '../../../global/error/AppMedError.ts';
import EICFFillablePdfCounterSigner from './EICFFillablePdfCounterSigner.tsx';

surveyLocalization.supportedLocales = ['en', 'fr'];

const EICFConsentSignPage: React.FC = observer(() => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const consentId = id ? Number(id) : 0;

  // Tab the user came from (set by EICFPendingSignaturesPage row buttons).
  const fromTab = (location.state as { fromTab?: 'pending' | 'signed' } | null)
    ?.fromTab;

  const {
    data: pending,
    isLoading: isLoadingPending,
    refetch,
  } = useGetPendingPhysicianEICFConsents();
  const { data: signed, isLoading: isLoadingSigned } =
    useGetSignedPhysicianEICFConsents();
  const submitSignature = useSubmitEICFPhysicianSignature();
  const downloadPdf = useDownloadEICFConsentPdf();
  const sendToAssistant = useSendEICFConsentToAssistant();
  const [error, setError] = useState<string | null>(null);
  const [completed, setCompleted] = useState(false);

  const consent: IEICFConsent | undefined = useMemo(() => {
    return (
      pending?.find((c) => c.id === consentId) ??
      signed?.find((c) => c.id === consentId)
    );
  }, [pending, signed, consentId]);

  const isReadOnly = consent?.status === EICFConsentStatus.Signed;
  const isFillablePdf =
    consent?.templateKind === EICFConsentTemplateKind.FillablePdf;

  // Tab to land on when going back. Priority: explicit fromTab from the row
  // click → infer from consent status → default to pending.
  const backTab: 'pending' | 'signed' =
    fromTab ?? (isReadOnly ? 'signed' : 'pending');
  const goBackToList = (tab: 'pending' | 'signed' = backTab) =>
    navigate('/hcpDashboard/eicfPending', { state: { tab } });

  // The HCP only reviews the patient's submitted form; counter-signature is a button click,
  // not a survey field. Always render in display mode and hide the survey's complete button.
  const survey: SurveyModel | null = useMemo(() => {
    if (!consent?.templateRawJson) return null;
    try {
      const model = new Model(consent.templateRawJson);
      model.applyTheme(PlainLightPanelless);
      model.locale = i18n.language;
      if (consent.answersJson) {
        try {
          model.data = JSON.parse(consent.answersJson);
        } catch (e) {
          console.error('Failed to parse answersJson', e);
        }
      }
      model.mode = 'display';
      model.showCompleteButton = false;
      return model;
    } catch (e) {
      console.error('Failed to load consent survey JSON', e);
      return null;
    }
  }, [consent, i18n.language]);

  const handleSign = async () => {
    if (!consent) return;
    setError(null);
    try {
      await submitSignature.mutateAsync({
        consentID: consent.id,
        answersJson: '',
      });
      setCompleted(true);
      await refetch();
    } catch (e) {
      if (e instanceof AppMedError) setError(e.message);
      else setError(t('eicf.page.unexpectedError'));
    }
  };

  const handleSignClick = () => {
    modals.openConfirmModal({
      title: t('eicf.hcp.signConfirmTitle'),
      children: <Text size="sm">{t('eicf.hcp.signConfirmBody')}</Text>,
      labels: {
        confirm: t('button.confirm'),
        cancel: t('button.cancel'),
      },
      confirmProps: { color: 'green' },
      onConfirm: () => {
        void handleSign();
      },
    });
  };

  if (isLoadingPending || isLoadingSigned) {
    return (
      <Container py="xl">
        <Loader />
      </Container>
    );
  }

  if (completed) {
    return (
      <Container py="xl">
        <Alert
          icon={<IconCheck size="1rem" />}
          color="green"
          title={t('eicf.hcp.signedTitle')}
        >
          {t('eicf.hcp.signedBody')}
        </Alert>
        <Group mt="md">
          <Button onClick={() => goBackToList('signed')}>
            {t('eicf.hcp.backToList')}
          </Button>
        </Group>
      </Container>
    );
  }

  if (!consent) {
    return (
      <Container py="xl">
        <Alert
          icon={<IconAlertCircle size="1rem" />}
          color="yellow"
          title={t('eicf.hcp.notFoundTitle')}
        >
          {t('eicf.hcp.notFoundBody')}
        </Alert>
        <Group mt="md">
          <Button onClick={() => goBackToList()}>
            {t('eicf.hcp.backToList')}
          </Button>
        </Group>
      </Container>
    );
  }

  return (
    <Container py="lg">
      <Stack gap="md">
        <Group justify="space-between">
          <Title order={2}>
            {i18n.language === 'fr'
              ? consent.templateNameFr
              : consent.templateNameEn}
          </Title>
          {isReadOnly && (
            <Badge color="green" variant="light">
              {t('eicf.hcp.signedBadge')}
            </Badge>
          )}
        </Group>
        <Text size="sm" c="dimmed">
          {t('eicf.hcp.patientLabel', { name: consent.patientName })} ·{' '}
          {t('eicf.page.versionLabel', { version: consent.templateVersion })}
        </Text>
        {error && (
          <Alert
            icon={<IconAlertCircle size="1rem" />}
            color="red"
            title={t('eicf.page.errorTitle')}
          >
            {error}
          </Alert>
        )}
        {isFillablePdf ? (
          isReadOnly ? (
            consent.signedPdfUrl ? (
              <iframe
                title={t('eicf.view.signedPdfTitle')}
                src={consent.signedPdfUrl}
                style={{ width: '100%', height: '75vh', border: 'none' }}
              />
            ) : (
              <Text c="dimmed">{t('eicf.view.pdfUnavailable')}</Text>
            )
          ) : (
            <EICFFillablePdfCounterSigner
              consent={consent}
              onSigned={async () => {
                setCompleted(true);
                await refetch();
              }}
            />
          )
        ) : (
          <Paper p="md" withBorder>
            {survey ? (
              <SurveyComponent model={survey} />
            ) : (
              <Text c="red">{t('eicf.page.surveyLoadError')}</Text>
            )}
          </Paper>
        )}
        <Group>
          <Button variant="default" onClick={() => goBackToList()}>
            {t('eicf.hcp.backToList')}
          </Button>
          {!isReadOnly && !isFillablePdf && (
            <Button
              color="green"
              onClick={handleSignClick}
              loading={submitSignature.isPending}
            >
              {t('eicf.hcp.signButton')}
            </Button>
          )}
          {consent && consent.status === EICFConsentStatus.Signed && (
            <>
              <Button
                variant="light"
                leftSection={<IconDownload size={16} />}
                onClick={() =>
                  downloadPdf.mutate({
                    id: consent.id,
                    language: i18n.language,
                    patientName: consent.patientName,
                    templateName:
                      i18n.language === 'fr'
                        ? consent.templateNameFr
                        : consent.templateNameEn,
                  })
                }
                loading={downloadPdf.isPending}
              >
                {t('eicf.actions.downloadPdf')}
              </Button>
              <Tooltip
                label={t(
                  'eicf.actions.sendToAssistantDisabledTooltip',
                )}
                disabled={consent.hasAssistantConfigured}
                withArrow
              >
                <Button
                  variant="light"
                  color="grape"
                  leftSection={<IconMail size={16} />}
                  onClick={() =>
                    sendToAssistant.mutate({
                      id: consent.id,
                      language: i18n.language,
                    })
                  }
                  loading={sendToAssistant.isPending}
                  disabled={!consent.hasAssistantConfigured}
                  data-disabled={!consent.hasAssistantConfigured || undefined}
                >
                  {t('eicf.actions.sendToAssistant')}
                </Button>
              </Tooltip>
            </>
          )}
        </Group>
      </Stack>
    </Container>
  );
});

export default EICFConsentSignPage;
